import type { Day, Transport, CustomEntry, Place, PlaceDetails } from '@my-fullstack-app/itinerary-types';
import { addMinutes, DEFAULT_START_TIME, parseTime, sortByStartTime, timeToMinutes } from '../components/itinerary/time-utils';
import { eachDayBetween, toIsoDateString } from '../lib/dates';
import { generateId } from '../lib/id';

export { addMinutes, DEFAULT_START_TIME, generateId, parseTime, sortByStartTime, timeToMinutes };

/**
 * Build a `Place` from catalog `PlaceDetails`. Single source of truth
 * for the `PlaceDetails → Place` field mapping shared by `addPlace`
 * (day-scheduled) and `addWishlistItem` (unscheduled). ADR-0044: writes
 * both legacy (placeId / photoRef) and neutral (catalogId / photoToken)
 * fields; both id and photoToken carry the 'google:' source prefix that
 * the catalog registry parses to dispatch lookup / photo calls.
 *
 * `startTime`/`endTime` are passed in (scheduled places) or left
 * undefined (wishlist candidates gain times only when promoted to a day).
 */
export function placeFromDetails(
  details: PlaceDetails,
  opts?: { id?: string | undefined; startTime?: string | undefined; endTime?: string | undefined },
): Place {
  const catalogId = details.placeId ? `google:${details.placeId}` : undefined;
  const photoToken =
    details.placeId && details.photoRef
      ? `google:${details.placeId}|${details.photoRef}`
      : undefined;
  return {
    id: opts?.id ?? generateId(),
    placeId: details.placeId,
    name: details.name,
    photoRef: details.photoRef,
    photoUrl: details.photoUrl,
    catalogId,
    photoToken,
    photoAttribution: details.photoAttribution,
    address: details.address,
    lat: details.lat,
    lng: details.lng,
    phone: details.phone,
    website: details.website,
    types: details.types,
    openingHours: details.openingHours,
    startTime: opts?.startTime,
    endTime: opts?.endTime,
  };
}

/**
 * Default start time ("HH:mm") for a new item appended to a day: the
 * latest existing end time, floored at 09:00, or `DEFAULT_START_TIME`
 * for an empty day. Shared by `addPlace` and `moveWishlistItemToDay` so
 * the slotting logic stays identical across both insert paths.
 */
export function nextDayStartTime(day: Day | undefined): string {
  if (!day?.places.length) return DEFAULT_START_TIME;
  const ends = day.places.map((p) => {
    const start = p.startTime ?? DEFAULT_START_TIME;
    const end = p.endTime ?? addMinutes(start, 60);
    return timeToMinutes(end);
  });
  const maxMins = Math.max(...ends, 9 * 60);
  return `${String(Math.floor(maxMins / 60)).padStart(2, '0')}:${String(maxMins % 60).padStart(2, '0')}`;
}

/**
 * In-place patch helper. Slice updaters use this instead of
 * `{ ...target, ...patch }` because their `patch` args are typed
 * `Patch<T>` / `ClearablePatch<T>`; spreading blindly would widen
 * target's required fields under EOPT. Convention (matches the
 * API-side `applyPatch`):
 *  - `undefined` / omitted → no-op (leave the field as-is),
 *  - `null` → clear the field (delete the key, restoring its absent
 *    default),
 *  - anything else (incl. `''` / `[]`) → assignment.
 */
export function applyPatch<T extends object>(target: T, patch: { [K in keyof T]?: T[K] | null | undefined }): T {
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (value === null) {
      delete (target as Record<string, unknown>)[key];
      continue;
    }
    (target as Record<string, unknown>)[key] = value;
  }
  return target;
}

export function createEmptyDays(startDate: string, endDate: string): Day[] {
  const dates = eachDayBetween(startDate, endDate);
  return dates.map((date) => ({
    id: generateId(),
    date: toIsoDateString(date),
    places: [],
    transports: [] as Transport[],
    customs: [] as CustomEntry[],
    hours: '0:00 – 24:00',
  }));
}
